import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError } from "../api/client";
import { useViewer } from "../auth/authContext";
import type { WalletState } from "../types";

interface Material {
  mnemonic: string;
  bip39Passphrase?: string;
  derivationPath?: string;
}
const EMPTY: Material = { mnemonic: "", bip39Passphrase: "" };
const MASK = "••••••••";
const UNKNOWN_SAVE =
  "Save outcome is unknown. Wallet status is being refreshed. Check the saved address before trying again.";

export function WalletEditor({
  wallet,
  onImported,
}: {
  wallet: WalletState;
  onImported: () => void;
}) {
  const viewer = useViewer();
  const [editing, setEditing] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [material, setMaterial] = useState<Material>(EMPTY);
  const [password, setPassword] = useState("");
  const authority = useRef("");
  const deadline = useRef(0);
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dialog = useRef<HTMLElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [pending, setPending] = useState(false);
  const [saving, setSaving] = useState(false);
  const importing = useRef(false);
  const mounted = useRef(true);
  const reload = useRef(onImported);
  reload.current = onImported;
  const [error, setError] = useState<string | null>(null);
  const configured = wallet.configured;
  function clear() {
    generation.current++;
    controller.current?.abort();
    clearTimeout(timer.current);
    authority.current = "";
    deadline.current = 0;
    setPassword("");
    setMaterial(EMPTY);
    setPreview(null);
    setConfirmed(false);
    setUnlocked(false);
    setPending(false);
  }
  function cancel() {
    if (importing.current) return;
    clear();
    setEditing(false);
    setError(null);
  }
  useEffect(() => {
    const requests = generation;
    mounted.current = true;
    return () => {
      mounted.current = false;
      requests.current++;
      controller.current?.abort();
      clearTimeout(timer.current);
      authority.current = "";
      deadline.current = 0;
      if (importing.current) reload.current();
    };
  }, []);
  useEffect(() => {
    const revoke = () => {
      clear();
      setError(importing.current ? UNKNOWN_SAVE : null);
      if (importing.current) reload.current();
    };
    window.addEventListener("auto-pools:unauthorized", revoke);
    return () => {
      window.removeEventListener("auto-pools:unauthorized", revoke);
    };
  }, []);
  useEffect(() => {
    if (!editing) return;
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.querySelector<HTMLElement>("input, button")?.focus();
    return () => previous?.focus();
  }, [editing, unlocked]);
  function edit(next: Material) {
    if (importing.current) return;
    generation.current++;
    controller.current?.abort();
    setPending(false);
    setMaterial(next);
    setPreview(null);
    setConfirmed(false);
  }
  async function request(action: "reveal" | "preview" | "import", event?: FormEvent) {
    event?.preventDefault();
    if (importing.current) return;
    if (action === "import" && (!preview || !confirmed)) return;
    if (
      configured &&
      action !== "reveal" &&
      (!authority.current || Date.now() >= deadline.current)
    ) {
      clear();
      setError("Editor expired. Enter your password again.");
      return;
    }
    const id = ++generation.current;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    setPending(true);
    if (action === "import") {
      importing.current = true;
      setSaving(true);
    }
    setError(null);
    const valid = () => generation.current === id && !abort.signal.aborted;
    try {
      if (action === "reveal") {
        // Expiry starts before the request, so a delayed response cannot extend authority.
        deadline.current = Date.now() + 5 * 60_000;
        authority.current = password;
        setPassword("");
        timer.current = setTimeout(() => {
          clear();
          setError(importing.current ? UNKNOWN_SAVE : "Editor expired. Enter your password again.");
          if (importing.current) reload.current();
        }, 5 * 60_000);
        const result = await api<Material>("/wallet/reveal", {
          method: "POST",
          body: { password: authority.current },
          sensitive: true,
          passwordReauthentication: true,
          signal: abort.signal,
        });
        if (!valid() || Date.now() >= deadline.current) return;
        if (!result.derivationPath) throw new Error("Saved derivation path is unavailable");
        setMaterial(result);
        setUnlocked(true);
      } else {
        const result = await api<{ address: string } | undefined>(`/wallet/${action}`, {
          method: "POST",
          sensitive: true,
          passwordReauthentication: configured,
          signal: abort.signal,
          body: {
            ...material,
            ...(configured ? { password: authority.current } : {}),
            ...(action === "import" ? { expectedAddress: preview } : {}),
          },
        });
        if (!valid()) return;
        if (action === "preview") {
          setPreview(result?.address ?? null);
          setConfirmed(false);
        } else {
          clear();
          setEditing(false);
        }
      }
    } catch (reason) {
      if (!valid()) return;
      if (action === "import") {
        clear();
        setError(UNKNOWN_SAVE);
        return;
      }
      if (
        action === "reveal" ||
        (reason instanceof ApiError && [401, 403, 429].includes(reason.status))
      )
        clear();
      setError(reason instanceof Error ? reason.message : "Wallet action failed");
    } finally {
      // A save may commit even when observation is aborted or the editor unmounts.
      if (action === "import") {
        importing.current = false;
        if (mounted.current) setSaving(false);
        reload.current();
      }
      if (valid()) setPending(false);
    }
  }
  const form = (
    <form className="stack vault-form" onSubmit={(event) => void request("preview", event)}>
      <h3>{configured ? "Edit saved wallet" : "Import first wallet"}</h3>
      <label>
        Seed phrase
        <textarea
          disabled={saving}
          rows={3}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          value={material.mnemonic}
          onChange={(event) => {
            edit({ ...material, mnemonic: event.target.value });
          }}
          required
        />
      </label>
      <label>
        BIP39 passphrase (if used)
        <input
          type="password"
          disabled={saving}
          autoComplete="off"
          value={material.bip39Passphrase ?? ""}
          onChange={(event) => {
            edit({ ...material, bip39Passphrase: event.target.value });
          }}
        />
      </label>
      {preview ? (
        <div className="address-confirm">
          {configured && (
            <>
              <p>Current address</p>
              <code>{wallet.address ?? "Unavailable"}</code>
            </>
          )}
          <p>Derived address</p>
          <code>{preview}</code>
          <label>
            <input
              type="checkbox"
              disabled={saving}
              checked={confirmed}
              onChange={(event) => {
                setConfirmed(event.target.checked);
              }}
            />
            I verified this address against an independent source and backup
          </label>
          <button
            className="button primary wide"
            type="button"
            disabled={!confirmed || pending || saving}
            onClick={() => void request("import")}
          >
            {saving ? "Encrypting…" : "Save encrypted wallet"}
          </button>
        </div>
      ) : (
        <button className="button ghost wide" disabled={pending || saving}>
          {pending ? "Deriving address…" : "Save and Preview Address"}
        </button>
      )}
      <p className="hint">
        {configured
          ? "The saved derivation path is preserved. Editor access expires after five minutes."
          : "The server uses the standard derivation path for a first import."}
      </p>
    </form>
  );
  // Reveal, preview and import all need the operator: a viewer gets no editor at all.
  if (viewer) return null;
  return (
    <>
      {configured ? (
        <div className="stack saved-wallet">
          <span>Seed phrase saved</span>
          <button
            className="button ghost"
            aria-label="Edit saved seed phrase"
            onClick={() => {
              setEditing(true);
            }}
          >
            {MASK}
          </button>
          <button
            className="button ghost"
            aria-label="Edit saved BIP39 passphrase"
            onClick={() => {
              setEditing(true);
            }}
          >
            BIP39 passphrase:{" "}
            {wallet.hasBip39Passphrase === undefined
              ? "Unavailable"
              : wallet.hasBip39Passphrase
                ? MASK
                : "Not set"}
          </button>
          <button
            className="button ghost"
            onClick={() => {
              setEditing(true);
            }}
          >
            Edit
          </button>
        </div>
      ) : (
        form
      )}
      {configured && editing && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) cancel();
          }}
        >
          <section
            ref={dialog}
            className="modal action-modal wallet-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wallet-editor-title"
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                cancel();
              }
              if (event.key !== "Tab") return;
              const controls = [
                ...event.currentTarget.querySelectorAll<HTMLElement>(
                  "button:not(:disabled), input:not(:disabled), textarea:not(:disabled)",
                ),
              ];
              const first = controls[0],
                last = controls.at(-1);
              if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
              } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
              }
            }}
          >
            <header className="modal-header">
              <h2 id="wallet-editor-title">Saved wallet</h2>
              <button
                className="icon-button"
                disabled={saving}
                onClick={cancel}
                aria-label="Cancel wallet edit"
              >
                ×
              </button>
            </header>
            {unlocked ? (
              form
            ) : (
              <form className="stack" onSubmit={(event) => void request("reveal", event)}>
                <label>
                  Current account password
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                    }}
                    required
                  />
                </label>
                <button className="button primary" disabled={!password || pending || saving}>
                  {pending ? "Unlocking…" : "Unlock wallet editor"}
                </button>
              </form>
            )}
            {error && (
              <p className="inline-alert error" role="alert">
                {error}
              </p>
            )}
          </section>
        </div>
      )}
      {!configured && error && (
        <p className="inline-alert error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
